import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Check, Mic, X } from 'lucide-react-native';
import React, { memo, useEffect, useRef, useState } from 'react';
import { Modal, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  Easing,
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useTheme } from 'styled-components';

export type VoiceRecordingOverlayProps = {
  visible: boolean;
  onCancel: () => void;
  onComplete: () => void;
};

const formatElapsed = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const VoiceRecordingOverlay = memo(
  ({ visible, onCancel, onComplete }: VoiceRecordingOverlayProps) => {
    return (
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={onCancel}
      >
        {visible && (
          <RecordingContent onCancel={onCancel} onComplete={onComplete} />
        )}
      </Modal>
    );
  },
);

const RecordingContent = memo(
  ({
    onCancel,
    onComplete,
  }: {
    onCancel: () => void;
    onComplete: () => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const [elapsed, setElapsed] = useState(0);
    const startedAtRef = useRef(Date.now());

    useEffect(() => {
      startedAtRef.current = Date.now();
      setElapsed(0);
      const id = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      return () => clearInterval(id);
    }, []);

    const pulse = useSharedValue(0);
    const innerPulse = useSharedValue(0);

    useEffect(() => {
      pulse.value = withRepeat(
        withTiming(1, { duration: 1600, easing: Easing.out(Easing.quad) }),
        -1,
        false,
      );
      innerPulse.value = withRepeat(
        withTiming(1, { duration: 1100, easing: Easing.inOut(Easing.quad) }),
        -1,
        true,
      );
      return () => {
        cancelAnimation(pulse);
        cancelAnimation(innerPulse);
      };
    }, [pulse, innerPulse]);

    const outerRingStyle = useAnimatedStyle(() => ({
      opacity: interpolate(pulse.value, [0, 1], [0.45, 0]),
      transform: [{ scale: interpolate(pulse.value, [0, 1], [1, 1.65]) }],
    }));

    const middleRingStyle = useAnimatedStyle(() => ({
      opacity: interpolate(pulse.value, [0, 1], [0.7, 0.05]),
      transform: [{ scale: interpolate(pulse.value, [0, 1], [1, 1.35]) }],
    }));

    const innerCircleStyle = useAnimatedStyle(() => ({
      transform: [{ scale: interpolate(innerPulse.value, [0, 1], [1, 1.06]) }],
    }));

    const accent = theme.link;
    const circleSize = rpx(180);
    const ringSize = rpx(220);

    return (
      <View style={[StyleSheet.absoluteFill, styles.backdrop]}>
        <View style={styles.topSpacer} />

        <View style={styles.circleWrap}>
          <Animated.View
            pointerEvents="none"
            style={[
              {
                position: 'absolute',
                width: ringSize,
                height: ringSize,
                borderRadius: ringSize / 2,
                backgroundColor: accent,
              },
              outerRingStyle,
            ]}
          />
          <Animated.View
            pointerEvents="none"
            style={[
              {
                position: 'absolute',
                width: ringSize,
                height: ringSize,
                borderRadius: ringSize / 2,
                backgroundColor: accent,
              },
              middleRingStyle,
            ]}
          />
          <Animated.View
            style={[
              {
                width: circleSize,
                height: circleSize,
                borderRadius: circleSize / 2,
                backgroundColor: accent,
                alignItems: 'center',
                justifyContent: 'center',
                shadowColor: accent,
                shadowOpacity: 0.5,
                shadowRadius: rpx(20),
                shadowOffset: { width: 0, height: rpx(8) },
              },
              innerCircleStyle,
            ]}
          >
            <Mic color="#fff" size={rpx(56)} strokeWidth={2.4} />
          </Animated.View>
        </View>

        <View style={styles.timerWrap}>
          <Typography.Label
            color="#fff"
            size={rpx(40)}
            style={{ letterSpacing: 1, fontVariant: ['tabular-nums'] }}
          >
            {formatElapsed(elapsed)}
          </Typography.Label>
          <Typography.Label
            regular
            color="rgba(255,255,255,0.7)"
            size={rpx(13)}
            style={{ marginTop: rpx(8) }}
          >
            正在录音…
          </Typography.Label>
        </View>

        <View style={[styles.actions, { paddingBottom: rpx(40) }]}>
          <ActionButton
            label="取消"
            color="rgba(255,255,255,0.18)"
            onPress={onCancel}
            icon={<X color="#fff" size={rpx(28)} strokeWidth={2.5} />}
            size={rpx(64)}
          />
          <ActionButton
            label="完成"
            color="#fff"
            onPress={onComplete}
            icon={<Check color={accent} size={rpx(28)} strokeWidth={3} />}
            size={rpx(64)}
          />
        </View>
      </View>
    );
  },
);

const ActionButton = memo(
  ({
    icon,
    label,
    color,
    onPress,
    size,
  }: {
    icon: React.ReactNode;
    label: string;
    color: string;
    onPress: () => void;
    size: number;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ alignItems: 'center' }}>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={onPress}
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: color,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </TouchableOpacity>
        <Typography.Label
          regular
          color="rgba(255,255,255,0.85)"
          size={rpx(13)}
          style={{ marginTop: rpx(10) }}
        >
          {label}
        </Typography.Label>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.78)',
    alignItems: 'center',
  },
  topSpacer: {
    flex: 1,
  },
  circleWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  timerWrap: {
    alignItems: 'center',
    marginTop: 32,
  },
  actions: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-evenly',
    width: '100%',
    paddingHorizontal: 48,
  },
});
